<template>
	<div class="experience-item">
		<div class="experience-head" @click="toggleExperience">
			<div class="first-row">
				<div class="title-row">
					<img v-if="showHandle" class="handle" :src="'https://img.qromo.io/img/vue/icons/icon-sort-list-item'+(userStore.darkMode ? '-dark' : '')+'.svg'" :title="getString('drag_to_sort')" :alt="getString('drag_to_sort')" draggable="false" @click.stop @mousedown="closeExperience" @touchstart="closeExperience">
					<p>{{ experience.name.length > 0 ? experience.name : getString('new_experience') }}</p>
				</div>
				<AppChevronIcon :direction="experience.activeDropdown ? 'up' : 'down'"/>
			</div>
			<div class="second-row" v-if="!isNewExperience">
				<div class="infos-container">
					<div class="people-container">
						<div class="elements-container">
							<div class="title">{{ getString('elements') }}</div>
							<div class="value">{{ experience.tables.length }}</div>
						</div>
					</div>
					<div class="price-container">
						<div class="title">{{ getString('price') }}</div>
						<div class="value">{{ readableEurValue(experience.price / 100) }}</div>
					</div>
					<div class="warning-label" v-if="checkExperienceSeatCount">
						<div class="warning-icon" v-html="svgStore.get('iconWarning')"></div>
					</div>
				</div>
				<div class="actions-container">
					<AvailabilityDot :type="1" v-if="!experience.active"/>
				</div>
			</div>
		</div>
		<div class="experience-body">
			<AppDropdown :collapsed="!experience.activeDropdown">
				<div class="experience-dropdown">
					<AppList :items="experienceItems">
						<template #slug-slot="{item}">
							<div class="row centered space-between text-slot">
								<p class="list-item-title">{{ item.title }}</p>
								<div class="text-input-container">
									<AppTextInput :value="item.value" :placeholder="item.placeholder" @changed-text="item.callback"
												  @changing-text="sanitizeSlug" @focusout="setUniqueSlug"/>
									<AppInPlaceCloud @close-cloud="closeSlugCloud" :type="-2" :active="activeWarningCloud">
										<p>{{ getString('product_url_warn') }}</p>
									</AppInPlaceCloud>
								</div>
							</div>
						</template>
						<template #warning-slot>
							<div v-if="checkExperienceSeatCount" class="warning-label">
								<div class="warning-icon" v-html="svgStore.get('iconWarning')"></div>
								{{ getString('zero_seats_warning') }}
							</div>
						</template>
					</AppList>
				</div>
			</AppDropdown>
		</div>
	</div>
</template>

<script>
import {useExperiencesStore, useRoomsStore, useUserStore} from "@/console-new/stores";
import {useLanguageStore, useSvgStore} from "@/shared/stores";
import AppList from "@/shared/components/AppList.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AvailabilityDot from "@/console-new/components/config/menu/AvailabilityDot.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppInPlaceCloud from "@/shared/components/AppInPlaceCloud.vue";

export default {
	components: {AppInPlaceCloud, AppTextInput, AvailabilityDot, AppChevronIcon, AppButtonIcon, AppDropdown, AppList, AppSwitch},
	setup() {
		const experiencesStore = useExperiencesStore();
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		const svgStore = useSvgStore();
		const roomsStore = useRoomsStore();
		return {getString: languageStore.getString, readableEurValue: languageStore.readableEurValue, experiencesStore, userStore, svgStore, roomsStore};
	},
	name: "ExperienceItem",
	data() {
		return {
			activeWarningCloud: false,
			warningCloudTimeout: null,
		}
	},
	props: {
		experienceId: {
			type: String,
			required: true
		}
	},
	computed: {
		toggleExperience() {
			if (this.experience.activeDropdown) {
				this.experience.activeDropdown = false;
			} else {
				this.experiencesStore.businessExperiences.forEach(experience => {
					experience.activeDropdown = false;
				});
				this.experience.activeDropdown = true;
			}
		},
		experience() {
			return this.experiencesStore.businessExperiences.find(experience => experience.experience_id === this.experienceId);
		},
		isNewExperience() {
			return this.experience.experience_id.startsWith('newexperience');
		},
		showHandle() {
			return this.experiencesStore.businessExperiences && this.experiencesStore.businessExperiences.filter(e => !e.deleted).length > 1;
		},
		experienceItems() {
			const nameItem = {
				title: this.getString('experience_name'),
				value: this.experience.name,
				textInput: true,
				placeholder: this.getString('experience_name'),
				callbackText: (v) => {
					this.experience.name = v;
				},
			};

			const deleteItem = {
				title: this.getString('delete_experience'),
				buttonTitle: this.getString('delete'),
				callbackButton: () => {
					if (this.isNewExperience) {
						this.experiencesStore.businessExperiences = this.experiencesStore.businessExperiences.filter(experience => experience.experience_id !== this.experience.experience_id);
					} else {
						this.experience.deleted = true;
					}
				},
			};

			if (this.isNewExperience) {
				return [nameItem, deleteItem];
			}

			let items = [
				{
					title: this.getString('available'),
					switch: true,
					active: this.experience.active,
					callback: (v) => {
						this.experience.active = v;
					},
				},
				nameItem,
				{
					title: this.getString('experience_url'),
					customSlot: "slug-slot",
					value: this.experience.slug,
					placeholder: this.getString('experience_url'),
					callback: (v) => {
						this.experience.slug = v;
					},
				},
				{
					title: this.getString('experience_description'),
					value: this.experience.description,
					textInput: true,
					isTextArea: true,
					placeholder: this.getString('experience_description'),
					callbackText: (v) => {
						this.experience.description = v;
					},
				},
				{
					title: this.getString('price'),
					value: this.experience.price / 100,
					numberInput: true,
					symbol: '€',
					min: 0,
					max: 1000000,
					doubleDecimal: true,
					callbackNumber: (v) => {
						this.experience.price = v * 100;
					},
				},
				{
					title: this.getString('limit_people'),
					numberInput: true,
					min: 1,
					max: 1000,
					value: this.experience.limit_people ? this.experience.limit_people : 1,
					callbackNumber: (v) => {
						this.experience.limit_people = v > 0 ? v : null;
					},
				},
			];

			if (this.userStore.currBusiness && this.userStore.currBusiness.reservations_request_card) {
				items.push({
					title: this.getString('card_min_people'),
					numberInput: true,
					min: 1,
					max: this.experience.limit_people ? this.experience.limit_people : 1,
					value: this.experience.card_min_people ? this.experience.card_min_people : null,
					placeholder: this.getString('card_min_people_placeholder'),
					callbackNumber: (v) => {
						this.experience.card_min_people = v > 0 ? v : null;
					},
				});
			}

			items.push(
				{
					title: this.getString('associated_elements'),
					buttonTitle: this.getString('set_elements'),
					headerSlot: 'warning-slot',
					callbackButton: () => {
						this.experiencesStore.editElementsId = this.experience.experience_id;
					},
				},
				{
					title: this.getString('reservation_schedule'),
					buttonTitle: this.getString('set_schedule'),
					callbackButton: () => {
						this.experiencesStore.editScheduleId = this.experience.experience_id;
					},
				},
				{
					title: this.getString('additional_info'),
					buttonTitle: this.getString('set_info'),
					callbackButton: () => {
						this.experiencesStore.editInfoId = this.experience.experience_id;
					},
				},
				{
					title: this.getString('image'),
					imageSlot: true,
					maxBytes: 1024 * 400,
					image: this.experience.picture,
					explain: this.getString('suggested_res_1080_400'),
					callbackImage: (v) => {
						this.experience.picture = v && v.length > 0 ? v[0] : null;
					},
					callbackRemove: () => {
						this.experience.picture = null;
					},
				},
				{
					title: this.getString('color'),
					colorPicker: true,
					color: this.experience.color,
					callbackColor: (v) => {
						this.experience.color = v;
					},
				},
				deleteItem
			);

			return items;
		},
		checkExperienceSeatCount() {
			if (this.isNewExperience || !this.experiencesStore.businessExperiences || !this.experience || !this.experience.tables || !this.roomsStore.tables) {
				return false;
			}
			const totalSeats = this.experience.tables.reduce((acc, expTable) => {
				const roomTable = this.roomsStore.tables.find(t => Number(t.room_table_id) === Number(expTable.room_table_id));
				return acc + (roomTable?.seat_number || 0);
			}, 0);
			return totalSeats === 0;
		}
	},
	methods: {
		closeExperience() {
			// Collapse before SortableJS clones the row, so the dragged item isn't captured expanded.
			this.experience.activeDropdown = false;
		},
		replaceCharacter(text) {
			text = (text || "").toLowerCase();
			text = text.replaceAll(" ", "-");
			let regexList = [/[áàãâä]/gi, /[éèêë]/gi, /[íìîï]/gi, /[óòôõö]/gi, /[úùûü]/gi, /[ç]/gi, /[ñ]/gi, /\s/gi, /_/gi, /@/gi, /&/gi, /[^a-z0-9\-]/gi];
			let replaceList = ["a", "e", "i", "o", "u", "c", "n", "-", "-", "a", "e", "-"];
			for (let i = 0; i < regexList.length; i++) {
				text = text.replace(regexList[i], replaceList[i]);
			}
			text = text.normalize("NFD").toLowerCase();
			return text || null;
		},
		sanitizeSlug(value) {
			let originalText = (value || "").toLowerCase();
			this.experience.slug = this.replaceCharacter(value);
			if (this.experience.slug && this.experience.slug !== originalText) {
				this.activeWarningCloud = true;
				clearTimeout(this.warningCloudTimeout);
				this.warningCloudTimeout = setTimeout(() => {
					this.activeWarningCloud = false;
				}, 3000);
			}
		},
		setUniqueSlug() {
			this.sanitizeSlug(this.experience.slug);
			this.experience.slug = this.experiencesStore.getUniqueSlug(this.experience.experience_id, this.experience.slug);
		},
		closeSlugCloud() {
			this.activeWarningCloud = true;
			clearTimeout(this.warningCloudTimeout);
		},
	}
}
</script>

<style scoped lang="scss">
.experience-item {
	position: relative;
	padding: 8px 12px;

	.experience-head {
		cursor: pointer;

		.first-row {
			display: flex;
			justify-content: space-between;
			align-items: center;

			.title-row {
				display: flex;
				align-items: center;
				gap: 8px;

				.handle {
					width: 20px;
					height: 20px;
					cursor: grab;
					flex-shrink: 0;

					&:active {
						cursor: grabbing;
					}
				}
			}

			p {
				font-size: 16px;
				font-weight: 500;
				color: var(--text-color);
			}

		}

		.second-row {
			display: flex;
			justify-content: space-between;
			align-items: center;
			margin-top: 8px;

			.people-container {
				display: flex;
				gap: 12px;
				background-color: $alpha_2;
				border-radius: 7px;
				padding: 6px 8px;
				box-shadow: inset 0 1px 2px var(--color-alpha-4);

				.elements-container {
					position: relative;

					&:after {
						content: '';
						position: absolute;
						top: 0;
						right: -7px;
						width: 1px;
						height: 100%;
						background-color: var(--color-alpha-4);
					}

				}

				.tables-container, .elements-container {
					display: flex;
					align-items: center;
					gap: 5px;
					justify-content: space-between;
					min-width: 76px;

					.title {
						font-size: 12px;
						color: var(--text-color);
					}

					.value {
						font-size: 12px;
						font-weight: 500;
						color: var(--text-color);
					}
				}
			}

			.price-container {
				display: flex;
				align-items: center;
				gap: 4px;
				background-color: $alpha_2;
				border-radius: 7px;
				padding: 6px 8px;
				box-shadow: inset 0 1px 2px var(--color-alpha-4);

				.title {
					font-size: 12px;
					color: var(--text-color);
				}

				.value {
					font-size: 12px;
					font-weight: 500;
					color: var(--text-color);
				}
			}

			.infos-container {
				display: flex;
				gap: 12px;
			}

			.actions-container {
				display: flex;
				align-items: center;
			}

		}
	}
}

.text-slot .input-wrapper {
	max-width: 40%;
	margin-left: auto;
}

.text-input-container {
	position: relative;
	margin-left: auto;

	.input-wrapper {
		max-width: unset;
		width: 240px;
		margin-left: 0;

		@media (max-width: 1250px) {
			width: 190px;
		}
	}
}

.warning-label {
	background-color: rgba(var(--rgb-warn), 0.2);
	color: var(--color-warn);
	padding: 4px 8px;
	border-radius: 4px;
	font-size: 12px;
	text-align: center;
	font-weight: 500;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
}

.warning-icon {
	display: flex;
	align-items: center;
}

.warning-icon svg {
	width: 14px;
	height: 14px;
	fill: var(--color-warn) !important;
}
.warning-icon :deep(svg),
.warning-icon :deep(svg path) {
	width: 14px;
	height: 14px;
	fill: var(--color-warn) !important;
}
</style>